/**
 * StepBrief.tsx — Step 0: Campaign brief form
 *
 * Collects: topic, goal, tonePreference, totalDays, template, colorTheme,
 * dailyTime, timezone.
 *
 * On submit: POST /api/campaigns → returns campaign with storyArc.
 *
 * Template grid: reuses StepTemplatePicker from DistillPageV2.
 * Color theme: swatches derived from the selected template's approvedThemes.
 * Research button: disabled ("Coming soon").
 */

import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import StepTemplatePicker from "../DistillPageV2/StepTemplatePicker";
import { ANGLES }         from "../DistillPageV2/constants";
import type { CarouselTemplateMeta } from "../DistillPageV2/types";
import type { TemplateCategoryKey }  from "../DistillPageV2/constants";
import {
  card, primaryBtn, stepHeading, stepSub, textarea,
  ACCENT, ACCENT_08, ACCENT_12, spinnerStyle,
} from "../DistillPageV2/sharedStyles";
import type {
  CreateCampaignBody, GoalKey, AngleKey, SchedulePattern, WeekDay, CampaignSummary,
  ViabilityResult, ArcTemplateKey, ArcTemplateOption,
} from "./types";
import { TIMEZONE_OPTIONS } from "./types";

// ── Animated nudge ────────────────────────────────────────────────────────────

const NUDGE_MESSAGE = "You — campaign posts with a personal angle get 3–4× more reach";
const NUDGE_DELAY_MS = 1500;

function useNudge(text: string): string {
  const [displayed, setDisplayed] = useState("");
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const frameRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const firedRef = useRef(false);

  useEffect(() => {
    if (text.length === 0) {
      firedRef.current = false;
      setDisplayed("");
      if (timerRef.current) clearTimeout(timerRef.current);
      if (frameRef.current) clearInterval(frameRef.current);
      return;
    }

    if (firedRef.current) return;
    firedRef.current = true;

    timerRef.current = setTimeout(() => {
      let i = 0;
      frameRef.current = setInterval(() => {
        i += 1;
        setDisplayed(NUDGE_MESSAGE.slice(0, i));
        if (i >= NUDGE_MESSAGE.length) clearInterval(frameRef.current!);
      }, 22);
    }, NUDGE_DELAY_MS);

    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
      if (frameRef.current) clearInterval(frameRef.current);
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [text.length === 0]);

  return displayed;
}

// ── Props ─────────────────────────────────────────────────────────────────────

interface Props {
  templates:         CarouselTemplateMeta[];
  templatesLoaded:   boolean;
  onSubmit:          (body: CreateCampaignBody) => Promise<void>;
  isSubmitting:      boolean;
  submitError:       string | null;
  runningCampaigns?: CampaignSummary[];
}

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8085';

// ── Constants ─────────────────────────────────────────────────────────────────

const GOALS: { value: GoalKey; label: string }[] = [
  { value: 'build_authority',  label: 'Build authority'  },
  { value: 'grow_followers',   label: 'Grow followers'   },
  { value: 'generate_leads',   label: 'Generate leads'   },
];

// Ordered by typical LinkedIn usage frequency
const PATTERN_OPTIONS: { value: SchedulePattern; label: string; sub: string }[] = [
  { value: 'weekdays',        label: 'Weekdays',         sub: 'Mon – Fri'   },
  { value: 'mwf',             label: 'Mon · Wed · Fri',  sub: '3× per week' },
  { value: 'tt',              label: 'Tue · Thu',        sub: '2× per week' },
  { value: 'daily',           label: 'Every day',        sub: '7× per week' },
  { value: 'every_other_day', label: 'Every 2 days',     sub: '3–4× per week'},
  { value: 'weekly',          label: 'Once a week',      sub: '1× per week' },
  { value: 'custom',          label: 'Custom',           sub: 'Choose days' },
];

const WEEKDAY_OPTIONS: { value: WeekDay; abbr: string; full: string }[] = [
  { value: 'mon', abbr: 'Mon', full: 'Monday'    },
  { value: 'tue', abbr: 'Tue', full: 'Tuesday'   },
  { value: 'wed', abbr: 'Wed', full: 'Wednesday' },
  { value: 'thu', abbr: 'Thu', full: 'Thursday'  },
  { value: 'fri', abbr: 'Fri', full: 'Friday'    },
  { value: 'sat', abbr: 'Sat', full: 'Saturday'  },
  { value: 'sun', abbr: 'Sun', full: 'Sunday'    },
];

/** Rough calendar-span estimate for a given pattern + n posts. */
function estimatedSpan(pattern: SchedulePattern, customDays: WeekDay[], n: number): string {
  switch (pattern) {
    case 'daily':           return `${n} day${n !== 1 ? 's' : ''}`;
    case 'every_other_day': return `${(n - 1) * 2 + 1} days`;
    case 'weekly':          return `${n} week${n !== 1 ? 's' : ''}`;
    case 'weekdays':        return `~${Math.ceil(n / 5 * 7)} days`;
    case 'mwf':             return `~${Math.ceil(n / 3 * 7)} days`;
    case 'tt':              return `~${Math.ceil(n / 2 * 7)} days`;
    case 'custom':
      if (!customDays.length) return '';
      return `~${Math.ceil(n / customDays.length * 7)} days`;
  }
}

// ── Component ─────────────────────────────────────────────────────────────────

const StepBrief: React.FC<Props> = ({
  templates, templatesLoaded, onSubmit, isSubmitting, submitError,
  runningCampaigns = [],
}) => {
  const [topic,           setTopic]           = useState("");
  const nudgeText = useNudge(topic);
  const [goal,            setGoal]            = useState<GoalKey>("build_authority");
  const [tone,            setTone]            = useState<AngleKey>("thoughtful");
  const [totalDays,       setTotalDays]       = useState<number>(7);
  const [selectedTpl,     setSelectedTpl]     = useState<string | null>(null);
  const [colorTheme,      setColorTheme]      = useState<string | null>(null);
  const [dailyTime,       setDailyTime]       = useState("09:00");
  const [timezone,        setTimezone]        = useState("Asia/Kolkata");
  const [schedulePattern, setSchedulePattern] = useState<SchedulePattern>("weekdays");
  const [scheduleDays,    setScheduleDays]    = useState<WeekDay[]>([]);
  const [activeCategory,  setActiveCategory]  = useState<TemplateCategoryKey>("all");
  // Viability assess state
  const [viabilityResult, setViabilityResult] = useState<ViabilityResult | null>(null);
  const [isAssessing,     setIsAssessing]     = useState(false);
  const [assessError,     setAssessError]     = useState<string | null>(null);
  const [selectedArc,     setSelectedArc]     = useState<ArcTemplateKey | null>(null);

  const selectedTemplate = templates.find(t => t.id === selectedTpl) ?? null;
  const themeOptions     = selectedTemplate?.approvedThemes ?? [];

  // When user picks a template, auto-select its first theme
  const handlePickTemplate = (id: string) => {
    setSelectedTpl(id);
    const tpl = templates.find(t => t.id === id);
    setColorTheme(tpl?.approvedThemes?.[0]?.key ?? null);
  };

  const customOk  = schedulePattern !== 'custom' || scheduleDays.length > 0;
  // Submit requires: assessment done + arc chosen (single_post blocks via no arc picker) + template + colorTheme
  const canSubmit = (
    viabilityResult !== null &&
    selectedArc !== null &&
    selectedTpl !== null &&
    colorTheme !== null &&
    customOk
  );

  const toggleCustomDay = (day: WeekDay) =>
    setScheduleDays(prev =>
      prev.includes(day) ? prev.filter(d => d !== day) : [...prev, day]
    );

  const handleAssess = async () => {
    setIsAssessing(true);
    setAssessError(null);
    setViabilityResult(null);
    setSelectedArc(null);
    const token = localStorage.getItem('distill_auth_token') || localStorage.getItem('distill_guest_token');
    try {
      const { data } = await axios.post<ViabilityResult>(
        `${API_URL}/api/campaigns/assess`,
        { topic: topic.trim(), goal },
        { headers: token ? { Authorization: `Bearer ${token}` } : {} },
      );
      setViabilityResult(data);
      setSelectedArc(data.bestArc);
      setTotalDays(data.suggestedDays ?? 7);
    } catch (e: any) {
      setAssessError(
        e?.response?.data?.error ?? e?.message ?? 'Failed to assess brief — please try again',
      );
    } finally {
      setIsAssessing(false);
    }
  };

  const handleSubmit = () => {
    if (!canSubmit || !selectedTpl || !colorTheme) return;
    onSubmit({
      topic:           topic.trim(),
      goal,
      tonePreference:  tone,
      totalDays,
      templateIds:     [selectedTpl],
      colorTheme,
      dailyTime,
      timezone,
      schedulePattern,
      scheduleDays:    schedulePattern === 'custom' ? scheduleDays : undefined,
      arcTemplate:     selectedArc ?? undefined,
    });
  };

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: "20px" }}>

      {/* ── Heading ─────────────────────────────────────────────────────────── */}
      <div>
        <div style={stepHeading}>New Campaign</div>
        <div style={stepSub}>
          Describe your topic and we'll generate a 7-day narrative arc using AI.
        </div>
      </div>

      {/* ── Awareness banner: campaigns already in flight ───────────────────── */}
      {runningCampaigns.length > 0 && (
        <div
          data-testid="cp-running-banner"
          style={{
            background:   "#F4F2FF",
            border:       `1px solid ${ACCENT_12}`,
            borderRadius: "12px",
            padding:      "12px 14px",
            display:      "flex",
            alignItems:   "center",
            gap:          "12px",
            flexWrap:     "wrap",
          }}
        >
          <div style={{
            background: ACCENT, color: "#fff",
            width: 28, height: 28, borderRadius: "50%",
            display: "flex", alignItems: "center", justifyContent: "center",
            fontSize: 14, fontWeight: 800, flexShrink: 0,
          }}>
            {runningCampaigns.length}
          </div>
          <div style={{ flex: 1, minWidth: 200 }}>
            <div style={{ fontSize: 13, fontWeight: 700, color: "#444" }}>
              {runningCampaigns.length === 1
                ? "You have 1 campaign already in flight"
                : `You have ${runningCampaigns.length} campaigns already in flight`}
            </div>
            <div style={{
              fontSize: 12, color: "#777", marginTop: 2,
              overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
            }}>
              {runningCampaigns.slice(0, 3).map(c => c.name).join(' · ')}
              {runningCampaigns.length > 3 && ` · +${runningCampaigns.length - 3} more`}
            </div>
          </div>
          <Link
            to="/dashboard"
            style={{
              textDecoration: "none",
              color: ACCENT,
              fontSize: 13, fontWeight: 700,
              padding: "6px 12px", borderRadius: 8,
              background: "#fff",
              border: `1.5px solid ${ACCENT}`,
              fontFamily: "inherit",
              flexShrink: 0,
            }}
          >
            View all →
          </Link>
        </div>
      )}

      {/* ── Topic ──────────────────────────────────────────────────────────── */}
      <div style={card}>
        <div style={{ fontSize: "13px", fontWeight: 700, marginBottom: "8px", color: "#444" }}>
          Campaign topic <span style={{ color: "#E84F4F" }}>*</span>
        </div>
        <textarea
          data-testid="cp-topic-input"
          rows={4}
          placeholder="e.g. Founders who write daily on LinkedIn get zero traction"
          value={topic}
          onChange={e => setTopic(e.target.value)}
          style={{ ...textarea, minHeight: "90px" }}
        />
        <div style={{ fontSize: "12px", color: "#aaa", marginTop: "4px" }}>
          Be specific — the more context you give, the sharper the arc.
        </div>

        {/* ── Engagement nudge — typewriter, appears 1.5 s after first keystroke ── */}
        {nudgeText && (
          <div style={{
            marginTop:    "10px",
            display:      "inline-flex",
            alignItems:   "center",
            gap:          "6px",
            background:   ACCENT_08,
            border:       `1.5px solid ${ACCENT_12}`,
            borderRadius: "20px",
            padding:      "6px 12px",
            fontSize:     "12px",
            color:        "#444",
            fontWeight:   600,
            lineHeight:   1.4,
          }}>
            <span style={{ color: ACCENT, fontSize: 13 }}>✦</span>
            {nudgeText}
            {nudgeText.length < NUDGE_MESSAGE.length && (
              <span style={{
                display:       "inline-block",
                width:         "1px",
                height:        "12px",
                background:    ACCENT,
                marginLeft:    "1px",
                verticalAlign: "text-bottom",
                animation:     "blink 0.7s step-end infinite",
              }} />
            )}
          </div>
        )}
      </div>

      {/* ── Goal + Tone ────────────────────────────────────────────────────── */}
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "12px" }}>
        {/* Goal */}
        <div style={card}>
          <div style={{ fontSize: "13px", fontWeight: 700, marginBottom: "10px", color: "#444" }}>
            Campaign goal
          </div>
          <div style={{ display: "flex", flexDirection: "column", gap: "6px" }}>
            {GOALS.map(g => (
              <label key={g.value} style={{ display: "flex", alignItems: "center", gap: "8px", cursor: "pointer", fontSize: "14px" }}>
                <input
                  type="radio"
                  name="goal"
                  value={g.value}
                  checked={goal === g.value}
                  onChange={() => setGoal(g.value)}
                  style={{ accentColor: ACCENT }}
                />
                {g.label}
              </label>
            ))}
          </div>
        </div>

        {/* Tone + Days */}
        <div style={{ display: "flex", flexDirection: "column", gap: "12px" }}>
          <div style={card}>
            <div style={{ fontSize: "13px", fontWeight: 700, marginBottom: "10px", color: "#444" }}>
              Default tone
            </div>
            <div style={{ display: "flex", flexWrap: "wrap", gap: "6px" }}>
              {ANGLES.map(a => (
                <button
                  key={a.key}
                  onClick={() => setTone(a.key as AngleKey)}
                  style={{
                    background:   tone === a.key ? ACCENT    : ACCENT_08,
                    color:        tone === a.key ? "#fff"    : ACCENT,
                    border:       `1.5px solid ${tone === a.key ? ACCENT : "transparent"}`,
                    borderRadius: "8px",
                    padding:      "5px 12px",
                    fontSize:     "13px",
                    fontWeight:   600,
                    cursor:       "pointer",
                    fontFamily:   "inherit",
                  }}
                >
                  {a.label}
                </button>
              ))}
            </div>
          </div>

          <div style={{ ...card, opacity: 0.45, pointerEvents: "none" }}>
            <div style={{ fontSize: "13px", fontWeight: 700, marginBottom: "6px", color: "#444" }}>
              Campaign length
            </div>
            <div style={{ fontSize: "12px", color: "#888" }}>
              Set automatically after brief assessment
            </div>
          </div>
        </div>
      </div>

      {/* ── Assess Brief ──────────────────────────────────────────────────── */}
      <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
        <button
          data-testid="cp-assess-btn"
          onClick={handleAssess}
          disabled={isAssessing || topic.trim().length < 20}
          title={topic.trim().length < 20 ? "Add more detail to your topic first" : ""}
          style={{
            ...primaryBtn,
            background: isAssessing ? ACCENT_08 : ACCENT,
            color:      isAssessing ? ACCENT     : "#fff",
            border:     `1.5px solid ${ACCENT}`,
            opacity:    (isAssessing || topic.trim().length < 20) ? 0.55 : 1,
            cursor:     (isAssessing || topic.trim().length < 20) ? "not-allowed" : "pointer",
          }}
        >
          {isAssessing
            ? <><span style={spinnerStyle} />Assessing…</>
            : "Assess Brief →"
          }
        </button>
        {viabilityResult && !isAssessing && (
          <span style={{ fontSize: "12px", color: "#888" }}>
            Re-assess after editing topic or goal
          </span>
        )}
      </div>

      {/* ── Assess error ──────────────────────────────────────────────────── */}
      {assessError && (
        <div style={{
          background: "#FFF0F0", border: "1.5px solid #E84F4F",
          borderRadius: "12px", padding: "12px 14px",
          fontSize: "13px", color: "#C0392B",
        }}>
          {assessError}
        </div>
      )}

      {/* ── Viability result card ─────────────────────────────────────────── */}
      {viabilityResult && (
        <div
          data-testid="cp-viability-card"
          style={{
            ...card,
            border: `1.5px solid ${viabilityResult.viability === 'single_post' ? '#E84F4F' : ACCENT}`,
          }}
        >
          <div style={{ display: "flex", alignItems: "center", gap: "10px", marginBottom: "10px" }}>
            <span style={{
              background: viabilityResult.viability === 'campaign'      ? ACCENT
                        : viabilityResult.viability === 'short_series'  ? '#E87B4F'
                        : '#E84F4F',
              color: "#fff", borderRadius: "20px", padding: "3px 12px",
              fontSize: "12px", fontWeight: 700,
            }}>
              {viabilityResult.viability === 'campaign'     ? '✓ Campaign material'
             : viabilityResult.viability === 'short_series' ? '⚡ Short series'
             : '⚠ Single post'}
            </span>
            {viabilityResult.viability !== 'single_post' && (
              <span style={{ fontSize: "12px", color: "#666" }}>
                {viabilityResult.minDays}–{viabilityResult.maxDays} days of material
              </span>
            )}
          </div>
          <div style={{ fontSize: "14px", color: "#333", marginBottom: "10px", lineHeight: 1.5 }}>
            {viabilityResult.reason}
          </div>
          {viabilityResult.enrichmentSuggestions.length > 0 && (
            <div>
              <div style={{ fontSize: "12px", fontWeight: 700, color: "#666", marginBottom: "6px" }}>
                Add this to make it richer:
              </div>
              <ul style={{ margin: 0, paddingLeft: "18px", fontSize: "13px", color: "#555", lineHeight: 1.7 }}>
                {viabilityResult.enrichmentSuggestions.map((s, i) => (
                  <li key={i}>{s}</li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}

      {/* ── Arc template picker ───────────────────────────────────────────── */}
      {viabilityResult && viabilityResult.viability !== 'single_post' && viabilityResult.viableArcs.length > 0 && (
        <div style={card}>
          <div style={{ fontSize: "13px", fontWeight: 700, marginBottom: "12px", color: "#444" }}>
            Campaign arc <span style={{ color: "#E84F4F" }}>*</span>
          </div>
          <div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
            {viabilityResult.viableArcs.map((arc: ArcTemplateOption) => {
              const isSelected = selectedArc === arc.key;
              return (
                <button
                  key={arc.key}
                  data-testid={`cp-arc-template-${arc.key}`}
                  onClick={() => setSelectedArc(arc.key)}
                  style={{
                    textAlign:    "left",
                    background:   isSelected ? ACCENT_08 : "#FAFAF8",
                    border:       `1.5px solid ${isSelected ? ACCENT : "#E4E4E0"}`,
                    borderRadius: "10px",
                    padding:      "12px 14px",
                    cursor:       "pointer",
                    fontFamily:   "inherit",
                  }}
                >
                  <div style={{ display: "flex", alignItems: "center", gap: "8px", marginBottom: "4px" }}>
                    <span style={{ fontSize: "14px", fontWeight: 700, color: isSelected ? ACCENT : "#333" }}>
                      {arc.name}
                    </span>
                    {arc.fit === 'best' && (
                      <span style={{
                        background: ACCENT, color: "#fff",
                        borderRadius: "20px", padding: "1px 9px",
                        fontSize: "11px", fontWeight: 700,
                      }}>
                        Recommended
                      </span>
                    )}
                    {arc.fit === 'good' && (
                      <span style={{
                        background: ACCENT_08, color: ACCENT,
                        borderRadius: "20px", padding: "1px 9px",
                        fontSize: "11px", fontWeight: 600,
                        border: `1px solid ${ACCENT}`,
                      }}>
                        Good fit
                      </span>
                    )}
                  </div>
                  <div style={{ fontSize: "13px", color: "#666", lineHeight: 1.5 }}>
                    {arc.description}
                  </div>
                </button>
              );
            })}
          </div>
        </div>
      )}

      {/* ── Campaign length stepper ───────────────────────────────────────── */}
      {viabilityResult && viabilityResult.viability !== 'single_post' && (
        <div style={card}>
          <div style={{ fontSize: "13px", fontWeight: 700, marginBottom: "10px", color: "#444" }}>
            Campaign length
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
            <button
              data-testid="cp-days-minus"
              onClick={() => setTotalDays(d => Math.max(viabilityResult.minDays, d - 1))}
              disabled={totalDays <= viabilityResult.minDays}
              style={{
                width: "36px", height: "36px", borderRadius: "8px",
                background: ACCENT_08, border: `1.5px solid ${ACCENT}`,
                color: ACCENT, fontSize: "18px", fontWeight: 700,
                cursor: totalDays <= viabilityResult.minDays ? "not-allowed" : "pointer",
                opacity: totalDays <= viabilityResult.minDays ? 0.4 : 1,
                fontFamily: "inherit",
              }}
            >−</button>
            <div style={{ textAlign: "center", minWidth: "60px" }}>
              <span data-testid="cp-days-count" style={{ fontSize: "22px", fontWeight: 800, color: ACCENT }}>{totalDays}</span>
              <div style={{ fontSize: "11px", color: "#888" }}>days</div>
            </div>
            <button
              data-testid="cp-days-plus"
              onClick={() => setTotalDays(d => Math.min(viabilityResult.maxDays, d + 1))}
              disabled={totalDays >= viabilityResult.maxDays}
              style={{
                width: "36px", height: "36px", borderRadius: "8px",
                background: ACCENT_08, border: `1.5px solid ${ACCENT}`,
                color: ACCENT, fontSize: "18px", fontWeight: 700,
                cursor: totalDays >= viabilityResult.maxDays ? "not-allowed" : "pointer",
                opacity: totalDays >= viabilityResult.maxDays ? 0.4 : 1,
                fontFamily: "inherit",
              }}
            >+</button>
            <span style={{ fontSize: "12px", color: "#888", marginLeft: "4px" }}>
              ({viabilityResult.minDays}–{viabilityResult.maxDays} suggested range)
            </span>
          </div>
        </div>
      )}

      {/* ── Template picker ────────────────────────────────────────────────── */}
      {viabilityResult && viabilityResult.viability !== 'single_post' && (
      <div style={card}>
        <div style={{ fontSize: "13px", fontWeight: 700, marginBottom: "12px", color: "#444" }}>
          Carousel template <span style={{ color: "#E84F4F" }}>*</span>
        </div>
        <StepTemplatePicker
          templates={templates}
          templatesLoaded={templatesLoaded}
          templatesError={null}
          selectedTemplateId={selectedTpl}
          activeCategory={activeCategory}
          isStructureLoading={false}
          preferredSlideCount={null}
          setActiveCategory={setActiveCategory}
          onPickTemplate={handlePickTemplate}
          onRetry={() => {}}
          onBack={() => {}}
        />
      </div>
      )}

      {/* ── Color theme ────────────────────────────────────────────────────── */}
      {viabilityResult && viabilityResult.viability !== 'single_post' && themeOptions.length > 1 && (
        <div style={card}>
          <div style={{ fontSize: "13px", fontWeight: 700, marginBottom: "12px", color: "#444" }}>
            Color theme
          </div>
          <div style={{ display: "flex", gap: "10px", flexWrap: "wrap" }}>
            {themeOptions.map(t => (
              <button
                key={t.key}
                onClick={() => setColorTheme(t.key)}
                title={t.label}
                style={{
                  width:        "32px",
                  height:       "32px",
                  borderRadius: "50%",
                  background:   t.accent,
                  border:       colorTheme === t.key ? `3px solid ${ACCENT}` : "3px solid transparent",
                  outline:      colorTheme === t.key ? `2px solid ${ACCENT}` : "none",
                  cursor:       "pointer",
                  padding:      0,
                }}
              />
            ))}
          </div>
        </div>
      )}

      {/* ── Posting schedule ───────────────────────────────────────────────── */}
      {viabilityResult && viabilityResult.viability !== 'single_post' && (
      <div style={card}>
        <div style={{ fontSize: "13px", fontWeight: 700, marginBottom: "12px", color: "#444" }}>
          Posting schedule
        </div>

        {/* Frequency pills */}
        <div style={{ display: "flex", flexWrap: "wrap", gap: "6px", marginBottom: "12px" }}>
          {PATTERN_OPTIONS.map(p => {
            const active = schedulePattern === p.value;
            return (
              <button
                key={p.value}
                onClick={() => setSchedulePattern(p.value)}
                title={p.sub}
                style={{
                  background:   active ? ACCENT    : ACCENT_08,
                  color:        active ? "#fff"    : ACCENT,
                  border:       `1.5px solid ${active ? ACCENT : "transparent"}`,
                  borderRadius: "8px",
                  padding:      "6px 13px",
                  fontSize:     "13px",
                  fontWeight:   600,
                  cursor:       "pointer",
                  fontFamily:   "inherit",
                  lineHeight:   "1.3",
                }}
              >
                {p.label}
              </button>
            );
          })}
        </div>

        {/* Custom day toggles — only visible when pattern === 'custom' */}
        {schedulePattern === 'custom' && (
          <div style={{ marginBottom: "12px" }}>
            <div style={{ fontSize: "12px", color: "#888", marginBottom: "8px" }}>
              Pick at least one day
            </div>
            <div style={{ display: "flex", gap: "6px", flexWrap: "wrap" }}>
              {WEEKDAY_OPTIONS.map(d => {
                const on = scheduleDays.includes(d.value);
                return (
                  <button
                    key={d.value}
                    onClick={() => toggleCustomDay(d.value)}
                    title={d.full}
                    style={{
                      background:   on ? ACCENT    : "#F4F4F0",
                      color:        on ? "#fff"    : "#555",
                      border:       `1.5px solid ${on ? ACCENT : "#E4E4E0"}`,
                      borderRadius: "8px",
                      padding:      "5px 11px",
                      fontSize:     "13px",
                      fontWeight:   600,
                      cursor:       "pointer",
                      fontFamily:   "inherit",
                    }}
                  >
                    {d.abbr}
                  </button>
                );
              })}
            </div>
          </div>
        )}

        {/* Estimated span hint */}
        {(() => {
          const span = estimatedSpan(schedulePattern, scheduleDays, totalDays);
          return span ? (
            <div style={{ fontSize: "12px", color: "#888", marginBottom: "14px" }}>
              {totalDays} posts · {span}
            </div>
          ) : null;
        })()}

        {/* Divider */}
        <div style={{ borderTop: "1px solid #F0F0EC", marginBottom: "14px" }} />

        {/* Publish time + Timezone */}
        <div style={{ display: "grid", gridTemplateColumns: "1fr 2fr", gap: "12px" }}>
          <div>
            <div style={{ fontSize: "12px", fontWeight: 600, marginBottom: "6px", color: "#666" }}>
              Publish time
            </div>
            <input
              type="time"
              value={dailyTime}
              onChange={e => setDailyTime(e.target.value)}
              style={{
                border: "1.5px solid #E4E4E0", borderRadius: "8px",
                padding: "8px 10px", fontSize: "14px", fontFamily: "inherit",
                outline: "none", width: "100%", boxSizing: "border-box",
              }}
            />
          </div>
          <div>
            <div style={{ fontSize: "12px", fontWeight: 600, marginBottom: "6px", color: "#666" }}>
              Timezone
            </div>
            <select
              value={timezone}
              onChange={e => setTimezone(e.target.value)}
              style={{
                border: "1.5px solid #E4E4E0", borderRadius: "8px",
                padding: "8px 10px", fontSize: "14px", fontFamily: "inherit",
                outline: "none", width: "100%", background: "#fff",
                boxSizing: "border-box",
              }}
            >
              {TIMEZONE_OPTIONS.map(tz => (
                <option key={tz.value} value={tz.value}>{tz.label}</option>
              ))}
            </select>
          </div>
        </div>
      </div>
      )}

      {/* ── Research button (disabled) ─────────────────────────────────────── */}
      <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
        <button
          disabled
          title="Coming soon — web research integration is planned"
          style={{
            ...primaryBtn,
            background: "#E4E4E0",
            color:      "#aaa",
            cursor:     "not-allowed",
            opacity:    0.7,
          }}
        >
          🔍 Research topic
        </button>
        <span style={{ fontSize: "12px", color: "#aaa" }}>Coming soon</span>
      </div>

      {/* ── Error ──────────────────────────────────────────────────────────── */}
      {submitError && (
        <div style={{
          background: "#FFF0F0", border: "1.5px solid #E84F4F",
          borderRadius: "12px", padding: "14px 16px",
          fontSize: "14px", color: "#C0392B",
        }}>
          {submitError}
        </div>
      )}

      {/* ── Submit ─────────────────────────────────────────────────────────── */}
      <button
        data-testid="cp-generate-arc-btn"
        onClick={handleSubmit}
        disabled={!canSubmit || isSubmitting}
        style={{
          ...primaryBtn,
          opacity: (!canSubmit || isSubmitting) ? 0.5 : 1,
          cursor:  (!canSubmit || isSubmitting) ? "not-allowed" : "pointer",
          justifyContent: "center",
          width: "100%",
          padding: "14px",
          fontSize: "15px",
        }}
      >
        {isSubmitting
          ? <><span style={spinnerStyle} />Generating arc…</>
          : "Generate Campaign Arc →"
        }
      </button>
    </div>
  );
};

export default StepBrief;
